/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {HttpClient, HttpErrorResponse, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable, throwError} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {ComparePropVolRptRequest} from '../models/reports/compare-prop-vol-rpt-request.model';
import {VarianceMktDivSgmntRptRequest} from '../models/reports/variance-mkt-div-sgmnt-rpt-request.model';
import {VehVolAnalysisReportRequest} from '../models/reports/veh-vol-analysis-report-request.model';
import {ConfigService} from './config.service';

@Injectable({
  providedIn: 'root'
})

export class ReportService {
  constructor(private readonly configService: ConfigService,
              private readonly http: HttpClient) {
  }

  /**
   * Gets the Run Rates and Dates Audit Report
   * @param scenIds A comma separated string of scenario ids
   * @param startYear The start year of the report
   * @param endYear The end year of the report
   * @param gmid The gmid of the user
   * @param aggregateTos Boolean to aggregate by type of sale
   * @param removeRecidNa Boolean to remove records with RECID = NA
   * @param highRunRate Inputted high run rate for report tolerance
   */
  getRunRatesDatesAuditReport(scenIds: string, startYear: number, endYear: number, gmid: string, aggregateTos: boolean, removeRecidNa: boolean, highRunRate: number) {
    const params = new HttpParams()
    .set('scen-ids', scenIds)
    .set('start-year', startYear)
    .set('end-year', endYear)
    .set('gmid', gmid)
    .set('aggregate-tos', aggregateTos)
    .set('remove-recid-na', removeRecidNa)
    .set('high-run-rate', highRunRate);

    return this.http.get(`${this.configService.baseApiUrl}reports/run-rate-date-audit`, {params, responseType: 'blob'})
    .pipe(catchError(error => this.handleError(error)));
  }

  /**
   * Gets the Vehicle and Propulsion Volume Comparison Report
   * @param vehScenIds A comma separated string of vehicle scenario ids
   * @param propScenIds A comma separated string of propulsion scenario ids
   * @param startYear The start year of the report
   * @param endYear The end year of the report
   * @param gmid The gmid of the user
   * @param regions A comma separated string of regions
   * @param subregions A comma separated string of subregions
   * @param planningMarkets A comma separated string of planning markets
   * @param architectures A comma separated string of architectures
   * @param marketDivisions A comma separated string of market divisions
   * @param programCodes A comma separated string of program codes
   * @param brandfams A comma separated string of brand families
   * @param includeDetail Boolean to include detail in the report
   * @return The Vehicle and Propulsion Volume Comparison Report in a blob
   */
  getVehPropVolComparison(vehScenIds: string, propScenIds: string, startYear: number, endYear: number, gmid: string, regions: string,
                          subregions: string, planningMarkets: string, architectures: string, marketDivisions: string, programCodes: string,
                          brandfams: string, includeDetail: boolean) {
    const params = new HttpParams()
    .set('veh-scen-ids', vehScenIds)
    .set('prop-scen-ids', propScenIds)
    .set('start-year', startYear)
    .set('end-year', endYear)
    .set('gmid', gmid)
    .set('regions', regions)
    .set('subregions', subregions)
    .set('planning-markets', planningMarkets)
    .set('architectures', architectures)
    .set('market-divisions', marketDivisions)
    .set('program-codes', programCodes)
    .set('brandfams', brandfams)
    .set('include-detail', includeDetail);

    return this.http.get(`${this.configService.baseApiUrl}reports/veh-prop-vol-compare`, {params, responseType: 'blob'})
    .pipe(catchError(error => this.handleError(error)));
  }

  /**
   * Gets the Vehicle Volume Analysis Report
   * @param request  The request object for the report
   */
  getVehVolAnalysisReport(request: VehVolAnalysisReportRequest) {
    return this.http.post(`${this.configService.baseApiUrl}reports/veh-vol-analysis`, request, {responseType: 'blob'})
    .pipe(catchError(error => this.handleError(error)));
  }

  /**
   * Gets the vehicle portfolio report
   * @param fcorgs the list of fcorgs to include in the report
   * @param multipleSheets whether to get the workbook in multiple sheets or not
   * @param portType the selected port type
   */
  getVehPortfolioReport(fcorgs: string[], multipleSheets: boolean, portType: string) {
    const params = new HttpParams()
    .set('port-type', portType)
    .set('fcorgs', fcorgs.join(', '))
    .set('multiple-sheets', multipleSheets);

    return this.http.get(`${this.configService.baseApiUrl}reports/vehicle-portfolio`, {params, responseType: 'blob'})
    .pipe(catchError(error => this.handleError(error)));
  }

  /**
   * Gets the archived portfolio report
   * @param archiveName the name of the archive to get the report for
   */
  getArchivedPortfolioReport(archiveName: string) {
    const params = new HttpParams()
    .set('archive-name', archiveName);

    return this.http.get(`${this.configService.baseApiUrl}reports/archived-portfolio`, {params, responseType: 'blob'})
    .pipe(catchError(error => this.handleErrorBlob(error)));
  }

  /**
   * Gets the Variance by Market Division or Segment Report
   * @param request  The request object for the report
   */
  getVarianceByMktDivOrSgmntRpt(request: VarianceMktDivSgmntRptRequest) {
    return this.http.post(`${this.configService.baseApiUrl}reports/variance-mkt-div-sgmnt`, request, {responseType: 'blob'})
    .pipe(catchError(error => this.handleError(error)));
  }

  /**
   * Gets the Portfolio Variance Report
   * @param portType the selected portfolio type
   * @param fcorg the selected fcorg
   * @param gmid the gmid of the user
   */
  getPortfolioVarianceRpt(portType: string, fcorg: string, gmid: string) {
    const params = new HttpParams()
    .set('port-type', portType)
    .set('fcorg', fcorg)
    .set('gmid', gmid);

    return this.http.get(`${this.configService.baseApiUrl}reports/portfolio-variance`, {params, responseType: 'blob'})
    .pipe(catchError(error => this.handleError(error)));
  }

  /**
   * Gets the Propulsion Record Report
   * @param gmid The gmid of the user
   * @param workingScenId The working scenario id
   */
  getPropulsionRecordReport(gmid: string, workingScenId: number) {
    const params = new HttpParams()
    .set('gmid', gmid)
    .set('scen-id', workingScenId);

    return this.http.get(`${this.configService.baseApiUrl}reports/propulsion-record`, {params, responseType: 'blob'})
    .pipe(catchError(error => this.handleError(error)));
  }

  /**
   * Gets the data to populate filters for the Compare Prop volume report
   * @param gmid The gmid of the user
   * @param scenarioIds The scenario ids to get the filters for
   * @param isGlobal Whether the report is global or not
   */
  getFiltersForComparePropVolReport(gmid: string, scenarioIds: string, isGlobal: boolean) {
    const params = new HttpParams()
    .set('gmid', gmid)
    .set('scenario-ids', scenarioIds)
    .set('is-global', isGlobal);

    return this.http.get(`${this.configService.baseApiUrl}reports/compare-prop-vol/filters`, {params})
    .pipe(catchError(error => this.handleError(error)));
  }

  /**
   * Gets the Compare Propulsion Volume Report
   * @param request The request object for the report
   */
  getComparePropVolReport(request: ComparePropVolRptRequest) {
    return this.http.post(`${this.configService.baseApiUrl}reports/compare-prop-vol`, request, {responseType: 'blob'})
    .pipe(catchError(error => this.handleErrorBlob(error)));
  }

  /**
   * Gets the Engine Transmission Detail Report
   */
  getEngTranDtlReport() {
    return this.http.get(`${this.configService.baseApiUrl}reports/eng-tran-dtl`, {responseType: 'blob'})
    .pipe(catchError(error => this.handleErrorBlob(error)));
  }

  /**
   * Handles the errors produced by the backend
   * @param errorResponse The error produced by the API to be handled by the frontend
   * @return void
   */
  handleError(errorResponse: HttpErrorResponse): any {
    let errorMessage = 'An unexpected error occurred.';
    if (errorResponse.error?.message) {
      errorMessage = errorResponse.error.message;
    }
    return throwError(errorMessage);
  }

  /**
   * Handles the errors produced by the backend when the response type is a blob
   * @param err The error produced by the API to be handled by the frontend
   */
  public handleErrorBlob(err: HttpErrorResponse): Observable<any> {
    const reader: FileReader = new FileReader();
    const obs = new Observable((observer: any) => {
      reader.onloadend = () => {
        const result = JSON.parse(reader.result as string);
        observer.error(result.message);
        observer.complete();
      };
    });
    reader.readAsText(err.error);
    return obs;
  }
}
